import { isThisYear } from 'date-fns';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdDateRange, MdOutlineEditLocation } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import { isEmpty, isNil } from 'lodash-es';
import { useLocation } from 'react-router';
import { ROUTES } from '../../constants/Constants';
import { lazyReactNaiveRetry } from '../../constants/Lazy';
import useAppNavigation from '../../hooks/useAppNavigation';
import usePrelaunch from '../../hooks/usePrelaunch';
import useSearchModal from '../../hooks/useSearchModal';
import useTimezone from '../../hooks/useTimezone';
import { setSearchSelection } from '../../redux/ducks/StorageDucks';
import type {
  SearchSelectionType,
  SelectedPlaceType,
  SelectedTimeType,
} from '../../redux/ducks/StorageDucks.types';
import { setSearchModalOpen } from '../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../redux/reducers/RootReducer.types';
import PlaceSelect from '../inputs/PlaceSelect';
import CalendarSelection from '../layout/CalendarSelection';
import type { CalendarTimeSelectionType } from '../layout/CalendarSelection/CalendarSelection.types';
import Modal from './Modal';

const CustomMap = lazyReactNaiveRetry(() => import('../layout/Map'));

const SearchModal = () => {
  const { currentTimezone } = useTimezone();
  const { isPrelaunch } = usePrelaunch();
  const searchModal = useSearchModal();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [timeSelectionOpen, setTimeSelectionOpen] = useState<boolean>(true);
  const [placeSelectionOpen, setPlaceSelectionOpen] = useState<boolean>(false);
  const currentLocation = useLocation();
  const { navigate, toLocalizedRoute, isSameRoute } = useAppNavigation();

  const currentSearchSelection = useSelector(
    (state: RootReducerType) => state.storage.searchSelection,
  );

  const searchSel = useMemo(() => {
    if (currentSearchSelection) {
      return { ...currentSearchSelection };
    }

    return { time: { timezone: currentTimezone } };
  }, [currentSearchSelection]);

  const [newSearchSelection, setNewSearchSelection] = useState<
    SearchSelectionType | undefined
  >();

  const mapRef = useRef<any>(null);

  const previewMap = useMemo(() => {
    if (!searchModal || !searchModal.isOpen) {
      return null;
    }

    const zoomLevel = newSearchSelection?.placeLocation?.city
      ? 13
      : newSearchSelection?.placeLocation?.state
        ? 10
        : newSearchSelection?.placeLocation?.country
          ? 4
          : 2;
    return (
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <CustomMap
          id="search-modal-map"
          ref={mapRef}
          center={
            newSearchSelection?.placeLocation?.latlng ||
            newSearchSelection?.placeLocation?.state?.latlng ||
            newSearchSelection?.placeLocation?.country?.latlng
          }
          styled="rounded-lg border border-solid border-slate-300"
          zoom={zoomLevel}
          readOnly
        />
      </Suspense>
    );
  }, [newSearchSelection, searchModal]);

  useEffect(() => {
    if (!searchModal.isOpen) {
      setTimeSelectionOpen(true);
      setPlaceSelectionOpen(false);
      setNewSearchSelection(searchSel);
    }
  }, [searchModal.isOpen]);

  useEffect(() => {
    setNewSearchSelection(searchSel);
  }, [searchSel]);

  const setLocation = (place?: SelectedPlaceType) => {
    if (!isNil(newSearchSelection)) {
      setNewSearchSelection({
        ...newSearchSelection,
        placeLocation: place,
        bounds: undefined,
      });
    }
  };

  const setTime = (selectedTime?: CalendarTimeSelectionType) => {
    if (selectedTime?.startTime) {
      const newTime: SelectedTimeType = {
        startTime: selectedTime.startTime,
        endTime: selectedTime?.endTime,
        timezone: currentTimezone,
      };

      setNewSearchSelection({ ...newSearchSelection, time: newTime });
    } else {
      setNewSearchSelection({
        ...newSearchSelection,
        time: { timezone: currentTimezone },
      });
    }
  };

  const onSearch = () => {
    if (!isNil(newSearchSelection)) {
      dispatch(setSearchSelection(newSearchSelection));
      if (
        !isSameRoute(currentLocation.pathname, ROUTES.HOME_ROUTE) &&
        !isSameRoute(currentLocation.pathname, ROUTES.SEARCH_ROUTE)
      ) {
        navigate(toLocalizedRoute(ROUTES.SEARCH_ROUTE));
      }
    }

    setTimeSelectionOpen(true);
    setPlaceSelectionOpen(false);
    searchModal.onClose();
  };

  const selectedPeriod = useMemo(() => {
    if (isNil(newSearchSelection) || !newSearchSelection.time?.startTime) {
      return t('static.header.anyday_label');
    }

    const targetDate = new Date(newSearchSelection.time.startTime);

    let formatOptions: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      timeZone: currentTimezone,
    };
    if (!isThisYear(targetDate)) {
      formatOptions = {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: currentTimezone,
      };
    }

    let result = null;
    if (newSearchSelection.time?.endTime) {
      result = `${Intl.DateTimeFormat(i18n.language, formatOptions).format(
        targetDate,
      )} - ${Intl.DateTimeFormat(i18n.language, formatOptions).format(
        new Date(newSearchSelection.time.endTime),
      )}`;
    } else {
      result = Intl.DateTimeFormat(i18n.language, formatOptions).format(
        targetDate,
      );
    }

    if (!result) {
      return t('static.header.anyday_label');
    }

    return result;
  }, [newSearchSelection]);

  const selectedPlace: string = useMemo<string>(() => {
    if (isNil(newSearchSelection) || !newSearchSelection?.placeLocation) {
      return t('static.header.anywhere_label');
    }

    let result = null;

    if (!isEmpty(newSearchSelection.placeLocation?.city?.label)) {
      result = result
        ? `${result}, ${newSearchSelection.placeLocation?.city?.label}`
        : newSearchSelection.placeLocation?.city?.label;
    }

    if (!isEmpty(newSearchSelection.placeLocation?.state?.label)) {
      result = result
        ? `${result}, ${newSearchSelection.placeLocation?.state?.label}`
        : newSearchSelection.placeLocation?.state?.label;
    }

    if (!isEmpty(newSearchSelection.placeLocation?.country?.label)) {
      result = result
        ? `${result}, ${newSearchSelection.placeLocation?.country?.label}`
        : newSearchSelection.placeLocation?.country?.label;
    }

    if (!result) {
      return t('static.header.anywhere_label');
    }

    return result;
  }, [newSearchSelection]);

  const calendarSelection = useMemo<
    CalendarTimeSelectionType | undefined
  >(() => {
    if (!isNil(newSearchSelection) && newSearchSelection.time?.startTime) {
      return {
        startTime: newSearchSelection.time.startTime,
        endTime: newSearchSelection.time?.endTime,
      };
    }

    return undefined;
  }, [newSearchSelection?.time]);

  useEffect(() => {
    if (placeSelectionOpen) {
      setTimeSelectionOpen(false);
    }
  }, [placeSelectionOpen]);

  useEffect(() => {
    if (timeSelectionOpen) {
      if (placeSelectionOpen) {
        setPlaceSelectionOpen(false);
      }
    }
  }, [timeSelectionOpen]);

  const bodyContent = (
    <div className="flex flex-col gap-4">
      {!isPrelaunch && (
        <div className="flex flex-col items-center justify-center font-semibold">
          {!timeSelectionOpen ? (
            <div className="mb-4 flex w-full flex-col items-center justify-between gap-2 sm:flex-row">
              <button
                id="search-modal-time-selection"
                key="search-modal-time-selection"
                aria-label="search-modal-time-selection"
                data-test-id="search-modal-time-selection"
                data-umami-event="search-modal-time-selection"
                className="flex size-full grow cursor-pointer flex-row items-center justify-center gap-1 rounded-lg border border-solid border-slate-300 px-4 py-3 hover:bg-slate-100 sm:justify-between"
                onClick={() => setTimeSelectionOpen(true)}
                type="button"
              >
                <span className="line-clamp-1 grow overflow-hidden text-ellipsis break-all capitalize">
                  {selectedPeriod || t('static.header.anyday_label')}
                </span>
                <span className="flex flex-col items-center justify-center">
                  <MdDateRange size={24} />
                </span>
              </button>
            </div>
          ) : (
            <div className="relative mb-4 flex w-full flex-col items-center justify-center">
              <CalendarSelection
                data-test-id="search-time-selector"
                value={calendarSelection}
                onChange={setTime}
              />
            </div>
          )}
          {!placeSelectionOpen ? (
            <>
              <div className="flex w-full flex-col items-start justify-start font-semibold">
                <button
                  id="search-modal-place-selection"
                  key="search-modal-place-selection"
                  aria-label="search-modal-place-selection"
                  data-test-id="search-modal-place-selection"
                  data-umami-event="search-modal-place-selection"
                  className="mb-4 flex w-full cursor-pointer flex-row items-center justify-between gap-1 rounded-lg border border-solid border-slate-200 px-4 py-3 hover:bg-slate-100"
                  onClick={() => setPlaceSelectionOpen(true)}
                  type="button"
                >
                  <span className="line-clamp-1 grow overflow-hidden text-ellipsis break-all">
                    {selectedPlace}
                  </span>
                  <span className="flex flex-col items-center justify-center">
                    <MdOutlineEditLocation size={24} />
                  </span>
                </button>
              </div>
              <div
                className={`mt-4 hidden aspect-square w-full sm:aspect-video ${
                  !timeSelectionOpen && 'md:block'
                }`}
              >
                {previewMap}
              </div>
            </>
          ) : (
            <div className="mb-4 flex w-full flex-col items-center justify-center">
              <PlaceSelect
                data-test-id="search-place-selector"
                value={newSearchSelection?.placeLocation}
                onChange={setLocation}
              />
              <div className="mt-4 aspect-square w-full sm:aspect-video">
                {previewMap}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      id="SearchModal"
      setIsOpen={(open) => dispatch(setSearchModalOpen(open))}
      disabled={false}
      loading={false}
      isOpen={searchModal.isOpen}
      title={t('static.search.modal_title')}
      actionLabel={t('static.search.modal_submit_btn_label')}
      onClose={searchModal.onClose}
      onSubmit={onSearch}
      body={bodyContent}
    />
  );
};

export default SearchModal;
